import { call, put, select } from 'redux-saga/effects';
import { ApplicationsBySeason } from '../actions/applicationTreatments/types/state';
import {
  fetchingApplicationTreatments,
  receiveApplicationTreatments,
} from '../actions/applicationTreatments/actions';
import { getApplicationEventAggregates } from '../queries/applicationEventAggregates';
import {
  adaptUnifiedEventsToTreatments,
  UnifiedApplicationTreatmentEvent,
} from '../utils/applicationEventAdapter';
import { ApplicationTreatment } from '../types/ApplicationTreatment';
import { getFieldUuid } from '../selectors/fieldSelectorsNew';
import { requestUnifiedApplicationEvents } from '../requesters/applicationEventAggregatesRequester';
import { selectApplicationCardEnabled } from './applicationTreatment/utils';
import { getApplicationAnalyticsQuery } from '../queries/applicationAnalyticsQuery';
import { olConnectionV2 } from '../requesters/olConnection';
import { getArdDataFeatureSelector } from '../selectors/featureSelectors';
import { getSeasonTagWithWildcards } from '../utils/seasonUtils';

function* fetchLegacyTreatments(fieldUUID: string) {
  const olResult = yield call(olConnectionV2, getApplicationAnalyticsQuery(fieldUUID));

  const eventAggregates =
    olResult.data.field.applicationAnalytics.applicationEventAggregates;

  const applicationDataBySeason: ApplicationsBySeason = {};
  eventAggregates.forEach(
    (aggregate: { seasonCode: string; applicationData: ApplicationTreatment[] }) => {
      applicationDataBySeason[aggregate.seasonCode] = aggregate.applicationData;
    },
  );
  yield put(receiveApplicationTreatments(applicationDataBySeason));

  return applicationDataBySeason;
}

type EventsBySeasonTagIndex = Record<string, UnifiedApplicationTreatmentEvent[]>;

const getEventsBySeasonTag = (
  events: UnifiedApplicationTreatmentEvent[],
  isHarvestArdDataEnabled: boolean
) => events.reduce<EventsBySeasonTagIndex>(
  (accum, event) => {
    const seasonCode = isHarvestArdDataEnabled ?
      getSeasonTagWithWildcards(event.legacySeasonTag) :
      event.legacySeasonTag;

    if (!accum[seasonCode]) {
      accum[seasonCode] = [];
    }
    accum[seasonCode].push(event);
    return accum;
  }, {});

export function* getApplicationTreatments(fieldUUID: string) {
  yield put(fetchingApplicationTreatments());

  const applicationCardEnabled: boolean = yield select(selectApplicationCardEnabled);
  if (!applicationCardEnabled) {
    return yield* fetchLegacyTreatments(fieldUUID);
  }

  const fieldUuid: string = yield select(getFieldUuid);
  const isHarvestArdDataEnabled = yield select(getArdDataFeatureSelector);
  const resolvedFieldId = fieldUuid || fieldUUID;

  const events: UnifiedApplicationTreatmentEvent[] = yield call(
    requestUnifiedApplicationEvents,
    getApplicationEventAggregates({ fieldId: resolvedFieldId }),
  );

  const eventsBySeasonTag = getEventsBySeasonTag(events, isHarvestArdDataEnabled);

  const applicationsBySeason = Object.fromEntries(
    Object.entries(eventsBySeasonTag).map(([seasonTag, seasonEvents]) => [
      seasonTag,
      adaptUnifiedEventsToTreatments(seasonEvents, seasonTag),
    ]),
  ) as ApplicationsBySeason;

  yield put(receiveApplicationTreatments(applicationsBySeason));

  return applicationsBySeason;
}